import {
  json,
  redirect,
  type LinksFunction,
  type LoaderFunctionArgs,
  type MetaFunction,
} from '@remix-run/node'
import {
  Link,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  ShouldRevalidateFunction,
  isRouteErrorResponse,
  useLoaderData,
  useLocation,
  useRouteError,
} from '@remix-run/react'
import {
  captureRemixErrorBoundaryError,
  setUser as setSentryUser,
  withSentry,
} from '@sentry/remix'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { Analytics } from '@vercel/analytics/react'
import { SpeedInsights } from '@vercel/speed-insights/remix'
import { Provider, useSetAtom } from 'jotai'
import { useHydrateAtoms } from 'jotai/utils'
import posthog from 'posthog-js'
import { useEffect, useMemo } from 'react'
import type { EmbedWithBroker } from '~/atoms'
import {
  aCookieContact,
  aEmbed,
  aOrganization,
  aOriginalLoginUser,
  aUser,
} from '~/atoms'
import { Toaster } from '~/components/ui'
import type { CookieContact, ModelUser, Organization, User } from '~/models'
import { findEmbed, findUser, getRootOrganization } from '~/mongo.server'
import { createIdbPersister } from '~/utils'
import {
  contactCookie,
  deleteCookieTokenHeader,
  embedCookie,
  getCookieToken,
  getOrganizationSlug,
  hasHeaderToken,
  parseTokenUser,
} from '~/utils.server'
import { RouteChangeProvider, useEmbed } from './hooks'
import mapboxStyles from './styles/mapbox-gl.css?url'
import styles from './tailwind.css?url'

export const shouldRevalidate: ShouldRevalidateFunction = ({ currentUrl }) => {
  return (
    currentUrl.pathname === '/login' ||
    currentUrl.pathname === '/redirect' ||
    currentUrl.pathname === '/admin/profile' ||
    currentUrl.pathname === '/admin/settings'
  )
}

export const links: LinksFunction = () => [
  { rel: 'stylesheet', href: styles },
  { rel: 'stylesheet', href: mapboxStyles },
  { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
  { rel: 'preconnect', href: 'https://fonts.gstatic.com' },
  {
    rel: 'stylesheet',
    href: 'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&family=Source+Sans+3:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&display=swap',
  },
  {
    rel: 'icon',
    href: '/favicon.ico',
    sizes: 'any',
  },
  {
    rel: 'apple-touch-icon',
    href: '/apple-touch-icon.png',
  },
]

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  return [
    { title: 'Associate' },
    {
      property: 'og:image',
      content: data?.organization?.meta.share
        ? data.organization.meta.share.url
        : '/open-graph.png',
    },
  ]
}

type LoaderData = {
  organization: Organization
  user?: User
  originalLoginUser?: ModelUser
  cookieContact?: CookieContact
  embed?: EmbedWithBroker
}

export const loader = async ({ request }: LoaderFunctionArgs) => {
  let slug = getOrganizationSlug(request)

  const organization = await getRootOrganization({ slug })

  if (!organization) {
    throw new Response('Not Found', { status: 404 })
  }

  if (
    request.url.includes('/login') ||
    request.url.includes('/redirect') ||
    request.url.includes('/forgot-password')
  ) {
    return {
      organization,
      originalUrl: request.url,
    }
  }

  const { pathname, searchParams } = new URL(request.url)
  const redirectParam =
    pathname !== '/' && pathname !== '/login' ? `?redirect=${pathname}` : ''

  const token = await getCookieToken(request)
  if (
    request.url.includes('/admin') &&
    (!hasHeaderToken(request) || token === null)
  ) {
    return redirect(`/login${redirectParam}`)
  }

  let user = null
  let originalLoginUser = null
  if (token !== null) {
    const parsedUser = parseTokenUser(token)
    if (parsedUser !== null) {
      user = await findUser({
        _id: parsedUser._id,
        'organization._id': organization._id,
      })
      originalLoginUser = parsedUser.originalLoginUser

      if (user!.status === 'suspended' || user!.status === 'inactive') {
        return redirect(
          `/login?message=Your account is suspended or no longer active. Please contact your organization to inquire about your account.`,
          {
            headers: await deleteCookieTokenHeader(),
          }
        )
      }
    } else {
      return redirect(`/login${redirectParam}`, {
        headers: await deleteCookieTokenHeader(),
      })
    }
  }

  // Members Only Organization
  if (organization.options.membersOnly && !user) {
    return redirect(`/login${redirectParam}`)
  }

  const cookieContact = await contactCookie.parse(
    request.headers.get('Cookie') || ''
  )

  let embed: EmbedWithBroker | undefined
  const embedSlug = searchParams.get('embedSlug')
  const hostURL = searchParams.get('hostURL')
  const broker = searchParams.get('broker')

  if (embedSlug) {
    embed =
      (await findEmbed({
        filter: {
          'organization.slug': organization.slug,
          slug: embedSlug,
        },
      })) ?? undefined
  }

  if (embed) {
    if (hostURL) embed.hostURL = hostURL
    if (broker) embed.broker = broker
  }

  const data = {
    embed,
    organization,
    user,
    originalLoginUser,
    cookieContact,
    originalUrl: request.url,
  }

  if (!embedSlug) {
    return json(data, {
      headers: {
        'Set-Cookie': await embedCookie.serialize('', { maxAge: 0 }),
      },
    })
  }

  return json(data)
}

function DebugScreenSizes() {
  if (!import.meta.env.DEV) {
    return null
  }
  return (
    <div className="fixed left-0 top-0 z-[999] bg-red-500 px-2 py-1 text-white print:hidden">
      <span className="sm:hidden">xs</span>
      <span className="hidden sm:inline-block md:hidden">sm</span>
      <span className="hidden md:inline-block lg:hidden">md</span>
      <span className="hidden lg:inline-block xl:hidden">lg</span>
      <span className="hidden xl:inline-block 2xl:hidden">xl</span>
      <span className="hidden 2xl:inline-block">2xl</span>
    </div>
  )
}

function HydrationContainer({
  organization,
  user,
  originalLoginUser,
  cookieContact,
  embed,
}: {
  organization: Organization
  user?: User
  originalLoginUser?: ModelUser
  cookieContact?: CookieContact
  embed?: EmbedWithBroker
}) {
  useHydrateAtoms([[aOrganization, organization]])
  useHydrateAtoms([[aUser, user ?? null]])
  useHydrateAtoms([[aOriginalLoginUser, originalLoginUser ?? null]])
  useHydrateAtoms([[aCookieContact, cookieContact ?? null]])
  useHydrateAtoms([[aEmbed, embed ?? null]])

  const setUser = useSetAtom(aUser)
  const setOriginalLoginUser = useSetAtom(aOriginalLoginUser)

  useEffect(() => {
    setUser(user ?? null)
    setOriginalLoginUser(originalLoginUser ?? null)
  }, [user])

  return <Outlet />
}

function Document({
  organization,
  user,
  children,
}: {
  organization?: Organization
  user?: User
  children: React.ReactNode
}) {
  const { pathname } = useLocation()
  const preventSearchEngineIndexing = useMemo(() => {
    if (organization?.options?.preventSearchEngineIndexing) {
      return true
    } else if (
      pathname.startsWith('/admin') ||
      pathname.startsWith('/reports')
    ) {
      return true
    }
    return false
  }, [organization, pathname])

  const canonicalUrl = useMemo(() => {
    if (
      organization?.options.wordpressSiteUrl &&
      pathname.startsWith('/listings/')
    ) {
      return `${organization!.options.wordpressSiteUrl}${pathname}`.replace(
        '//listings',
        '/listings'
      )
    }
    return null
  }, [organization, pathname])

  return (
    <html lang="en" className="scroll-pt-40 overflow-x-hidden">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <Meta />
        {preventSearchEngineIndexing && (
          <meta name="robots" content="noindex, nofollow, noarchive" />
        )}
        <Links />
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      </head>
      <body
        className="!opacity-100 transition-opacity duration-300"
        style={{ opacity: 0 }}>
        <RouteChangeProvider>{children}</RouteChangeProvider>
        <ScrollRestoration />
        <script
          async
          src="https://maps.googleapis.com/maps/api/js?key=AIzaSyAv5MD0Ed191OORtaC3YkigbkXkWq3JnOY&libraries=places&callback=Function.prototype&loading=async"
        />
        <Scripts />

        {import.meta.env.PROD && (
          <>
            <Analytics />
            <SpeedInsights />
          </>
        )}

        {user && organization?.options.intercomEnabled && (
          <>
            <script
              dangerouslySetInnerHTML={{
                __html: `window.intercomSettings = {
              api_base: "https://api-iam.intercom.io",
              app_id: "a3v8n6l5",
              name: "${
                user ? `${user.firstName} ${user.lastName}` : undefined
              }",
              user_id: "${user?._id || undefined}",
              email: "${user?.email || undefined}",
              Subdomain: "${organization.slug}",
              Role: "${user.role}",
              Group: "${user.group}",
              created_at: ${undefined} // Signup date as a Unix timestamp
            };`,
              }}
            />
            <script
              dangerouslySetInnerHTML={{
                __html: `
              // We pre-filled your app ID in the widget URL: 'https://widget.intercom.io/widget/a3v8n6l5'
              (function(){var w=window;var ic=w.Intercom;if(typeof ic==="function"){ic('reattach_activator');ic('update',w.intercomSettings);}else{var d=document;var i=function(){i.c(arguments);};i.q=[];i.c=function(args){i.q.push(args);};w.Intercom=i;var l=function(){var s=d.createElement('script');s.type='text/javascript';s.async=true;s.src='https://widget.intercom.io/widget/a3v8n6l5';var x=d.getElementsByTagName('script')[0];x.parentNode.insertBefore(s,x);};if(document.readyState==='complete'){l();}else if(w.attachEvent){w.attachEvent('onload',l);}else{w.addEventListener('load',l,false);}}})();`,
              }}
            />
          </>
        )}
      </body>
    </html>
  )
}

const queryClient = new QueryClient()

function App() {
  const { organization, user, originalLoginUser, cookieContact, embed } =
    useLoaderData<LoaderData>()
  const location = useLocation()
  const { isEmbed } = useEmbed()

  useEffect(() => {
    posthog.capture('$pageview')
  }, [location.pathname])

  useEffect(() => {
    if (user) {
      // Test original logged in user
      posthog.identify(user.tid, {
        email: user.email,
        name: `${user.firstName} ${user.lastName}`,
      })
    }
  }, [])

  useEffect(() => {
    if (user) {
      setSentryUser({ email: user.email })
    } else {
      setSentryUser(null)
    }
  }, [user])

  useEffect(() => {
    const outer = document.createElement('div')
    outer.style.visibility = 'hidden'
    outer.style.width = '100px'
    document.body.appendChild(outer)

    const widthNoScroll = outer.offsetWidth
    outer.style.overflow = 'scroll'

    const inner = document.createElement('div')
    inner.style.width = '100%'
    outer.appendChild(inner)

    const widthWithScroll = inner.offsetWidth
    outer.parentNode?.removeChild(outer)

    if (widthNoScroll - widthWithScroll > 0) {
      document.documentElement.classList.add('scrollbar')
    }
  }, [])

  return (
    <Document organization={organization} user={user}>
      <DebugScreenSizes />
      <Provider>
        {isEmbed ? (
          <QueryClientProvider client={queryClient}>
            <HydrationContainer
              organization={organization}
              user={user}
              originalLoginUser={originalLoginUser}
              cookieContact={cookieContact}
              embed={embed}
            />
          </QueryClientProvider>
        ) : (
          <PersistQueryClientProvider
            client={queryClient}
            persistOptions={{
              persister: createIdbPersister(),
              maxAge: 1000 * 60 * 60 * 24 * 5,
            }}>
            <HydrationContainer
              organization={organization}
              user={user}
              originalLoginUser={originalLoginUser}
              cookieContact={cookieContact}
              embed={embed}
            />
          </PersistQueryClientProvider>
        )}
      </Provider>
      <Toaster />
    </Document>
  )
}

export default withSentry(App)

export function ErrorBoundary() {
  const error = useRouteError()
  const renderError = () => {
    if (isRouteErrorResponse(error)) {
      return (
        <div className="mx-auto mt-10 flex flex-col justify-around px-4 py-8 text-center ">
          <h1 className="my-4 text-2xl">
            {error.status} {error.statusText}
          </h1>

          <p className="py-4">
            The page you requested does not exist. Click{' '}
            <Link viewTransition to="/admin" className="underline">
              here
            </Link>{' '}
            to return to the dashboard.
          </p>
        </div>
      )
    } else if (error instanceof Error) {
      return (
        <div>
          <h1>Error</h1>
          <p>{error.message}</p>
          <p>The stack trace is:</p>
          <pre>{error.stack}</pre>
        </div>
      )
    } else {
      return <h1>Unknown Error</h1>
    }
  }
  if (import.meta.env.PROD) {
    captureRemixErrorBoundaryError(error)
  }
  return <Document>{renderError()}</Document>
}
